import type { SuperHubStore } from '..';
import type { GitSidebarCategory, GitSidebarQueryString } from '@readme/api/src/mappings/sidebar/types';

import React, { useEffect, useRef } from 'react';

import useReadmeApi from '@core/hooks/useReadmeApi';

import { useSuperHubStore } from '..';

/**
 * Map of route sections to the corresponding `/sidebar?page_type` endpoint.
 */
export const superHubSidebarTypes = {
  api_definitions: null,
  changelog: 'changelog',
  custom_components: null,
  discuss: null,
  docs: 'guide',
  home: null,
  page: 'custom_page',
  recipes: null,
  reference: 'reference',
  reusable_content: null,
} satisfies Record<NonNullable<SuperHubStore['routeSection']>, GitSidebarQueryString['page_type'] | null>;

interface ConnectSuperHubSidebarToApiProps {
  children: React.ReactNode;
  /**
   * Very first sidebar data that is populated during SSR and the first CSR.
   */
  initialSidebar: GitSidebarCategory[] | null;
}

/**
 * Connects our `SuperHubStore` sidebar slice to the git sidebar API endpoint to
 * continually hydrate its data and keeps it in sync.
 */
export function ConnectSuperHubSidebarToApi({ children, initialSidebar }: ConnectSuperHubSidebarToApiProps) {
  const [getApiBaseUrl, initialize, routeSection] = useSuperHubStore(s => [
    s.getApiBaseUrl,
    s.sidebar.initialize,
    s.routeSection,
  ]);
  const initialSidebarRef = useRef(initialSidebar);

  // Validate sidebar data only when a valid page type exists.
  const pageType = routeSection ? superHubSidebarTypes[routeSection] : null;
  const apiUrl = pageType ? `/sidebar?page_type=${pageType}` : null;
  const {
    data,
    error = null,
    etag,
    isLoading,
    swrKey,
  } = useReadmeApi<GitSidebarCategory[]>(apiUrl, {
    apiBaseUrl: getApiBaseUrl(),
    swr: {
      revalidateOnFocus: false,
      shouldRetryOnError: true,
    },
  });

  useEffect(() => {
    initialize({
      data: data || initialSidebarRef.current || [],
      error,
      etag,
      isLoading,
      swrKey,
    });

    // After first connection is established to our SWR endpoints, discard the
    // initial value refs b/c we no longer need it as a fallback.
    if (!isLoading) {
      initialSidebarRef.current = null;
    }
  }, [data, error, etag, initialize, isLoading, swrKey]);

  return <>{children}</>;
}
